import { googleFontsStylesheetUrl } from "@babackapp/editor-blocks-library/fonts";
import type { EmailsThemeDto } from "@babackapp/private-api-client/types";

type EmailFontTheme = Pick<EmailsThemeDto, "fontFamily" | "fontUrl">;

export type EmailFont = { name: string; fontFamily: string };

const SANS_FALLBACK = "Arial, Helvetica, sans-serif";
const NARROW_FALLBACK = "'Arial Narrow', Arial, sans-serif";
const SERIF_FALLBACK = "Georgia, 'Times New Roman', serif";

/** Installed on most devices: no web font to load. */
export const STANDARD_EMAIL_FONTS: EmailFont[] = [
  { name: "Arial", fontFamily: "Arial, Helvetica, sans-serif" },
  { name: "Helvetica", fontFamily: "Helvetica, Arial, sans-serif" },
  { name: "Verdana", fontFamily: "Verdana, Geneva, sans-serif" },
  { name: "Tahoma", fontFamily: "Tahoma, Geneva, sans-serif" },
  { name: "Trebuchet MS", fontFamily: "'Trebuchet MS', Helvetica, sans-serif" },
  { name: "Georgia", fontFamily: "Georgia, 'Times New Roman', serif" },
  { name: "Times New Roman", fontFamily: "'Times New Roman', Times, serif" },
  { name: "Courier New", fontFamily: "'Courier New', Courier, monospace" },
];

/** Google Fonts, each followed by the closest standard font. */
export const GOOGLE_EMAIL_FONTS: EmailFont[] = (
  [
    ["Roboto", SANS_FALLBACK],
    ["Roboto Condensed", NARROW_FALLBACK],
    ["Open Sans", SANS_FALLBACK],
    ["Lato", SANS_FALLBACK],
    ["Montserrat", SANS_FALLBACK],
    ["Poppins", SANS_FALLBACK],
    ["Inter", SANS_FALLBACK],
    ["Raleway", SANS_FALLBACK],
    ["Nunito", SANS_FALLBACK],
    ["Work Sans", SANS_FALLBACK],
    ["DM Sans", SANS_FALLBACK],
    ["Oswald", NARROW_FALLBACK],
    ["Playfair Display", SERIF_FALLBACK],
    ["Merriweather", SERIF_FALLBACK],
    ["Lora", SERIF_FALLBACK],
  ] as [string, string][]
).map(([name, fallback]) => ({ name, fontFamily: `'${name}', ${fallback}` }));

/**
 * Theme fields for a Google font typed by the merchant. `family` must be the
 * exact Google Fonts name (`googleFontFamily`): Google refuses any other case.
 */
export function customGoogleFontTheme(family: string): EmailFontTheme {
  return {
    fontFamily: `'${family}', ${SANS_FALLBACK}`,
    fontUrl: googleFontsStylesheetUrl([family]),
  };
}

export function googleFontTheme(font: EmailFont): EmailFontTheme {
  return {
    fontFamily: font.fontFamily,
    fontUrl: googleFontsStylesheetUrl([font.name]),
  };
}

export function standardFontTheme(font: EmailFont): EmailFontTheme {
  return { fontFamily: font.fontFamily, fontUrl: null };
}

export type EmailFontChoice =
  | { kind: "default" }
  | { kind: "standard"; name: string }
  | { kind: "google"; name: string }
  | { kind: "custom"; name: string };

/** Which picker entry the saved theme font matches. */
export function emailFontChoice(
  theme?: EmailFontTheme | null,
): EmailFontChoice {
  if (!theme?.fontFamily) return { kind: "default" };

  if (theme.fontUrl) {
    const family = /[?&]family=([^:&]+)/.exec(theme.fontUrl)?.[1];
    const name = family ? decodeURIComponent(family.replace(/\+/g, " ")) : "";
    return GOOGLE_EMAIL_FONTS.some((font) => font.name === name)
      ? { kind: "google", name }
      : { kind: "custom", name };
  }

  const standard = STANDARD_EMAIL_FONTS.find(
    (font) => font.fontFamily === theme.fontFamily,
  );
  return standard
    ? { kind: "standard", name: standard.name }
    : { kind: "default" };
}
